/* global localStorage, window */

import React, { forwardRef, startTransition, useCallback, useContext, useEffect, useState } from 'react';
import SearchPageProductListItem from './SearchPageProductListItem';
import { array, bool, func, node, number, object, string } from 'prop-types';
import { CgMenuGridR } from 'react-icons/cg';
import { FaList } from 'react-icons/fa';
import { ToastContext } from './context/ToastContext';
import NavigationSkeleton from './NavigationSkeleton';
import SearchPageSidebarSkeleton from './SearchPageSidebarSkeleton';
import SearchPageSidebar from './SearchPageSidebar';
import PageNav from './PageNav';
import { useNavigate } from 'react-router-dom';

const DUMMY_PRODUCTS_AMOUNT = 8;

const SearchPageProductList = forwardRef(({ isMobile, productList, searchLoading, collectionHandle, children, searchQuery, totalPages, curPage, paginateUser, setSearchQuery, generatedPagesData, source = 'Collection' }, ref) => {
    const [view, setView] = useState('grid');
    const [pageCount, setPageCount] = useState(0);
    const { sendMessage } = useContext(ToastContext);
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const navigate = useNavigate();

    const handleAddProduct = useCallback((productId) => {
        if (!selectedProductIds.includes(productId)) {
            setSelectedProductIds((prev) => [...prev, productId]);
        }
    }, [selectedProductIds]);
    const handleRemoveProduct = useCallback((productId) => {
        if (selectedProductIds.includes(productId)) {
            setSelectedProductIds((prev) => prev.filter((id) => id !== productId));
        }
    }, [selectedProductIds]);

    const onViewChange = view => {
        setView(view);

        if (typeof window !== 'undefined') {
            localStorage.setItem('view', view);
        }
    };

    useEffect(() => {
        const { page } = searchQuery;
        startTransition(() => {
            setPageCount(!page ? 0 : parseInt(page));
        });
    }, [searchQuery.page, searchQuery.limit]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const view = localStorage.getItem('view');

        if (view) {
            startTransition(() => {
                setView(view);
            });
        }
    }, []);

    useEffect(() => {
        setSelectedProductIds([]);
    }, [searchLoading]);

    const viewGrid = useCallback(() => {
        onViewChange('grid');
    }, []);

    const viewList = useCallback(() => {
        onViewChange('list');
    }, []);


    return (<ul
        ref={ref}
        className={'search-page--product-list'}>
        <div
            style={{
                display: 'flex',
                flexFlow: 'column wrap',
                gap: '1.25rem'
            }}>
            <div
                className={'search-page--header-container' + ((searchLoading || !productList.length) ? '' : ' loaded')}
            >
                {searchLoading ? (
                    <NavigationSkeleton />
                ) : null}
                {(!searchLoading && productList.length > 0) && (<>
                    {children?.props?.children[0]}
                    <div
                        style={{
                            display: 'flex',
                            gap: '10px',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexGrow: '1',
                        }}
                    >
                        {children?.props?.children[1]}
                        <div
                            className={'d-flex gap-2 align-items-center mx-3'}>
                            <p
                                className={'m-0'}><b>VIEW AS</b></p>
                            <button
                                onClick={viewGrid}
                                onKeyDown={viewGrid}
                                style={{ all: 'unset'}}>
                                <div
                                    className={`search-page-icon-wrapper ${view === 'grid' ? 'active-view' : ''}`}
                                    style={{ padding: '.25em' }}
                                >
                                    <CgMenuGridR
                                        fontSize={'32px'}
                                    />
                                </div>
                            </button>
                            <button
                                onClick={viewList}
                                onKeyDown={viewList}
                                style={{ all: 'unset' }}>
                                <div
                                    className={`search-page-icon-wrapper ${view === 'list' ? 'active-view' : ''}`}
                                >
                                    <FaList
                                        fontSize={'27px'}
                                    />
                                </div>
                            </button>
                        </div>
                    </div>
                    {searchLoading ? (
                        <SearchPageSidebarSkeleton />
                    ) : (
                        <>
                            {productList.length > 0 ? (
                                <SearchPageSidebar
                                    isMobile={isMobile}
                                    searchQuery={searchQuery}
                                    setSearchQuery={setSearchQuery}
                                />
                            ) : null}
                        </>
                    )}
                </>)}
            </div>
        </div>
        {(searchLoading === false && !productList.length) && (<p
            style={{ height: '24px' }}>No products match your search criteria, please try again.</p>)}
        <div
            className={`search-page--product-list-inner ${view}`}>
            {(productList.length > 0 && searchLoading === false) && productList.map((product, i) => (
                <SearchPageProductListItem
                    index={i}
                    id={product.id}
                    key={product.id}
                    isMobile={isMobile}
                    title={product.title}
                    handle={product.handle}
                    imageUrl={product.imageUrl}
                    price={product.price}
                    compareAtPrice={product.compareAtPrice}
                    availableForSale={product.availableForSale}
                    avgRating={product.avgRating}
                    totalReviews={product.totalReviews}
                    searchLoading={searchLoading}
                    collectionHandle={collectionHandle}
                    isFeatured={product.isFeatured}
                    inventoryLevelId={product?.availableInventoryLevels?.at(0)?.id ?? 0}
                    availableQuantity={product?.availableInventoryLevels?.at(0)?.availableQuantity ?? 0}
                    bypassNegativeQuantity={product?.availableInventoryLevels?.at(0)?.bypassNegativeQuantity ?? false}
                    view={view}
                    addProduct={handleAddProduct}
                    removeProduct={handleRemoveProduct}
                />
            ))}
            {searchLoading === true && new Array(DUMMY_PRODUCTS_AMOUNT).fill(0).map((item, idx) => (
                <SearchPageProductListItem
                    index={idx}
                    key={idx}
                    isMobile={isMobile}
                    searchLoading={searchLoading} />
            ))}
        </div>
        {!searchLoading && productList.length > 0 ? (<div
            className={'border d-flex justify-content-between align-items-center mt-2'}>
            <div>
                <PageNav
                    curPage={curPage}
                    totalPages={totalPages}
                    generatedPagesData={generatedPagesData}
                    paginateUser={paginateUser}
                />
            </div>
            <button
                onClick={() => {
                    if (selectedProductIds.length < 2) {
                        sendMessage('Please select at least two products to compare.', 'danger');
                        return;
                    }
                    if (selectedProductIds.length > 4) {
                        sendMessage('You can only compare a maximum of 4 products.', 'danger');
                        return;
                    }
                    const currentPath = window.location.pathname;
                    const newPath = `/compareProduct?ids=${selectedProductIds.join(',')}&source=${source}&path=${currentPath}`;
                    navigate(newPath);
                }}
                className={'custom-btn'}>
                Compare Selected
            </button>
        </div>) : null}
    </ul>);
});

SearchPageProductList.displayName = 'SearchPageProductList';

SearchPageProductList.propTypes = {
    isMobile: bool,
    productList: array,
    searchLoading: bool,
    collectionHandle: string,
    children: node,
    onLoadMoreClick: func,
    showLoadMoreButton: bool,
    loading: bool,
    searchQuery: object,
    totalPages: number,
    curPage: number,
    paginateUser: func,
    setSearchQuery: func,
    generatedPagesData: array,
    source: string,
};

SearchPageProductList.defaultProps = {
    collectionHandle: '',
};

export default SearchPageProductList;